import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PageHeader, Reveal } from "@/components/public/ui";
import { usePublic } from "@/hooks/usePublic";
import { mediaUrl } from "@/lib/media";

const RIDER = "https://images.unsplash.com/photo-1590506995460-d0d9892b54da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const RIDER2 = "https://images.unsplash.com/photo-1506424482693-1f123321fa53?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const LOBBY = "https://images.unsplash.com/photo-1718220268527-4477fd170775?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const TECH = "https://images.unsplash.com/photo-1777886290085-f5cb4fbc0f6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fallback = [
  { id: "1", title: "Aimway Station", category: "Product", image: "/aimway-logo.png" },
  { id: "2", title: "Rider Ready", category: "Lifestyle", image: RIDER },
  { id: "3", title: "UV Technology", category: "Technology", image: TECH },
  { id: "4", title: "Helmet Care", category: "Lifestyle", image: RIDER2 },
  { id: "5", title: "Corporate Placement", category: "Installations", image: LOBBY },
  { id: "6", title: "Sanitization Chamber", category: "Product", image: "/aimway-logo.png" },
];

export default function Gallery() {
  const { data } = usePublic("/gallery");
  const items = data && data.length ? data : fallback;
  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))], [items]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const shown = cat === "All" ? items : items.filter((i) => i.category === cat);

  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow="Gallery" title="Aimway in pictures" subtitle="Explore our machines, technology and real-world installations." />
      <section className="container-page py-16">
        <div className="mb-10 flex flex-wrap gap-3">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`rounded-full px-5 py-2 text-sm font-600 transition-colors ${cat === c ? "bg-brand text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`gallery-filter-${c.toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="columns-2 gap-4 md:columns-3">
          {shown.map((g, i) => (
            <Reveal key={g.id} delay={(i % 6) * 0.05} className="mb-4 break-inside-avoid">
              <button onClick={() => setActive(g)} className="group block w-full overflow-hidden rounded-2xl border border-slate-100 bg-brand-sky" data-testid={`gallery-item-${i}`}>
                <img src={mediaUrl(g.image)} alt={g.title} className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setActive(null)} className="fixed inset-0 z-[70] grid place-items-center bg-brand-navy/90 p-6 backdrop-blur-sm" data-testid="gallery-lightbox">
            <button className="absolute right-6 top-6 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white" onClick={() => setActive(null)}><X size={22} /></button>
            <motion.img initial={{ scale: 0.9 }} animate={{ scale: 1 }} src={mediaUrl(active.image)} alt={active.title} className="max-h-[85vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
